import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { ButtonHTMLAttributes, forwardRef } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    variant = "primary", 
    size = "md", 
    isLoading = false,
    className, 
    children,
    disabled,
    ...props 
  }, ref) => {
    const variants = {
      primary: "bg-primary-blue text-white hover:bg-[#244F73] shadow-md shadow-primary-blue/20",
      secondary: "bg-primary-orange text-white hover:bg-[#D88F3E]",
      outline: "border-2 border-primary-blue text-primary-blue hover:bg-primary-blue/5 bg-transparent",
      ghost: "text-primary-blue hover:bg-primary-blue/5 bg-transparent",
    };
    
    const sizes = {
      sm: "px-4 py-2 text-sm",
      md: "px-6 py-2.5 text-sm",
      lg: "px-8 py-4 text-base",
    };

    return (
      <button 
        ref={ref}
        className={cn(
          "rounded-lg font-bold transition-all flex items-center justify-center gap-2",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "active:scale-[0.98]",
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
